import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  CanSeeModulesMarketplaceDocument,
  CanSelfUpsellDocument,
  GetDatoModulesDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { MetaContext } from 'modules/core/lib/navigation/routes'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { locale as localeMap } from 'modules/marketplace/components/utils/locales'
import { countryCode } from 'modules/marketplace_modules/components/utils/countryCode'

type NavigationParam = Parameters<NavigationRoutesFn>[0]

const ModulesRootPage = React.lazy(
  () => import('modules/marketplace_modules/pages/ModulesRootPage')
)
const ModulesDetailTemplate = React.lazy(
  () => import('modules/marketplace_modules/pages/ModulesDetailTemplate')
)
const ModulesPage = React.lazy(() => import('modules/marketplace_modules/pages/ModulesPage'))
const RevampResourceHeader = React.lazy(
  () => import('modules/marketplace_modules/components/Revamp/ResourceHeader')
)

const ModulesIndexPage = () => {
  return <ModulesPage />
}

export const getMarketplaceModulesRoute = (navigation: NavigationParam) => {
  return {
    path: navigation.discover.marketplaceModules.path,
    element: <ModulesRootPage />,
    handle: {
      meta: () => ({
        title: i18n.t('sidebar.modules'),
      }),
    },
    children: [
      {
        index: true,
        element: <ModulesIndexPage />,
      },
      {
        path: 'all',
        element: <ModulesPage />,
      },
      {
        path: navigation.discover.marketplaceModules.item.path,
        element: <ModulesDetailTemplate />,

        handle: {
          resource: true,
          meta: ({
            params,
            watchQuery,
            session: {
              company,
              access: { locale: user_locale },
            },
          }: MetaContext) => {
            const slug = params['id']
            const datoFormatLocale = localeMap[user_locale]
            const countryCodeForCompany = countryCode(company.legalEntities[0]?.country || '')
            const modulesQuery = watchQuery({
              query: GetDatoModulesDocument,
              variables: {
                locale: datoFormatLocale,
                slug: slug,
                countryCode: countryCodeForCompany,
                preview: true,
              },
            })

            const moduleAuthQuery = watchQuery({
              query: CanSeeModulesMarketplaceDocument,
            })

            const selfUpsellQuery = watchQuery({
              query: CanSelfUpsellDocument,
            })

            return modulesQuery.flatMap(({ data: moduleData }) => {
              const module = moduleData?.modules.datoModulesConnection.nodes[0]

              return moduleAuthQuery.flatMap(({ data: moduleAuthData }) => {
                const canUpsell = moduleAuthData?.modules._policies.moduleManageModule.can

                return selfUpsellQuery.map(({ data: selfUpsellData }) => {
                  const canSubscriptionUpgrade =
                    selfUpsellData?.billing._policies.subscriptionUpgradeSubscription.can

                  const isFreeTrial = company.subscription.inFreeTrial

                  const canSeeSelfUpsell = !isFreeTrial && !!canSubscriptionUpgrade
                  const canSeeNormalUpsell = !isFreeTrial && !!canUpsell
                  const canRequestModule = !canUpsell

                  return {
                    title: module?.name || i18n.t('marketplace.modules.title'),
                    headerComponent: (
                      <Box
                        border={{
                          bottom: {
                            width: 's1',
                            style: 'solid',
                            color: 'grey200',
                          },
                        }}
                      >
                        <RevampResourceHeader
                          moduleData={module}
                          addonReference={module?.billingAddonReference || []}
                          crossSellingAddonReference={
                            module?.crossSellingBillingAddonReference || []
                          }
                          mandatoryCrossSellingAddonReference={
                            module?.mandatoryCrossSellingAddons || []
                          }
                          canSeeSelfUpsell={canSeeSelfUpsell}
                          canSeeNormalUpsell={canSeeNormalUpsell}
                          canRequestModule={canRequestModule}
                        />
                      </Box>
                    ),
                  }
                })
              })
            })
          },
        },
      },
    ],
  }
}
